import { t } from '@citrite/translate';
import { WorkspaceConfiguration } from '@citrite/workspace-ui-platform';
import { machineListCache } from 'App/Activity/MachineStateManager';
import { MachineRegistrationState, PowerState } from 'App/Activity/Network/ActionsUtil';
import { getLaunchMethod } from 'Environment/launchResource/clientManager';
import { launchMethod } from 'Environment/launchResource/constants';
import { isCitrixChromeApp } from 'Environment/launchResource/device';
import {
	LaunchParams,
	launchResource,
	LaunchStatus,
} from 'Environment/launchResource/launchResource';
import {
	ResourceContext,
	ResourceLatestStatusEntry,
	ResourceLatestStatusMap,
	ResourceLaunchProgressMap,
	TroubleshootFixStatus,
} from 'Workspace/ResourceProvider/Context';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';

export const nativeWorkspaceScheme = 'receiver:';

export enum LaunchMechanism {
	HTML5 = 'HTML5',
	NativeApplication = 'Native_Application',
	BrowserExtension = 'Browser_Extension',
	BrowserProtocolHandler = 'Browser_Protocol_Handler',
	BrowserIcaFileDownload = 'Browser_ICA_File_Download',
}

export function urlSafeBase64Encode(value: string) {
	// Replace certain characters in the base64 alphabet with ones that are safe to use in URL paths
	const encodedValue = window.btoa(value);
	return encodedValue.replace(/\+/g, '_').replace(/\//g, '!').replace(/=/g, '-');
}

// This must be calculated on demand as it may change within the lifetime of a page.
// We parse location.href rather than using location.hash because the latter behaves
// inconsistently (with regard to URL encoding/decoding) in Firefox.
function getUrlHash() {
	return window?.location?.href?.split('#')[1] || '';
}

export function isAppShortcutLaunch() {
	const urlHashComponents = getUrlHash().split('/');
	return urlHashComponents.length === 4 && urlHashComponents[1] === 'launch';
}

export const updateMachinePowerStatus = (resourceId: string) => {
	machineListCache.updateMachine(
		{ resourceId },
		{
			powerState: PowerState.ON,
			registrationState: MachineRegistrationState.REGISTERED,
			lastUpdatedTime: new Date().toISOString(),
		}
	);
};

//** This change needs to be removed when we will consume the error mapper module */
export enum ErrorType {
	CouldNotConnectToWorkstation = 'CouldNotConnectToWorkstation',
	NoMoreActiveSessions = 'NoMoreActiveSessions',
	UnavailableDesktop = 'UnavailableDesktop',
	UnavailableAppProtectedDesktop = 'UnavailableAppProtectedDesktop',
	WorkstationInMaintenance = 'WorkstationInMaintenance',
	GeneralAppLaunchError = 'GeneralAppLaunchError',
	ResourceAutoLaunchFailure = 'ResourceAutoLaunchFailure',
	DefaultLaunchError = 'DefaultLaunchError',
}
export interface ErrorContext {
	clientType?: string;
}

export function getErrorMessage(errorType: string, context?: ErrorContext): string {
	switch (errorType) {
		case ErrorType.CouldNotConnectToWorkstation:
			return t('Workspace:desktop_launch_failures.CouldNotConnectToWorkstation');

		case ErrorType.NoMoreActiveSessions:
			return t('Workspace:desktop_launch_failures.NoMoreActiveSessions');

		case ErrorType.UnavailableDesktop:
			return t('Workspace:desktop_launch_failures.UnavailableDesktop');

		case ErrorType.UnavailableAppProtectedDesktop:
			if (context?.clientType === 'html5') {
				return t('Workspace:app_Protection_launch_failures.Refer_Help_Page');
			} else {
				return t('Workspace:app_Protection_launch_failures.Use_Browser_Extension');
			}

		case ErrorType.WorkstationInMaintenance:
			return t('Workspace:desktop_launch_failures.WorkstationInMaintenance');

		case ErrorType.GeneralAppLaunchError:
			return t('Workspace:app_failed_launch');

		case ErrorType.ResourceAutoLaunchFailure:
			return t('Workspace:resource_autolaunch_failure');

		case ErrorType.DefaultLaunchError:
			return t('Workspace:app_failed_launch');

		default:
			return t('Workspace:app_failed_launch');
	}
}

export function getLatestResourceStatus(
	launchProgress: ResourceLaunchProgressMap,
	resourceLatestStatusMap: ResourceLatestStatusMap
): ResourceLatestStatusMap {
	if (!launchProgress) {
		return {};
	}

	const latestStatusMap = { ...resourceLatestStatusMap };

	for (const operationId in launchProgress) {
		const progress = launchProgress[operationId];

		const currentStatus = latestStatusMap[progress.resourceId];

		if (!currentStatus || progress.timeStamp > currentStatus.timeStamp) {
			const preservedTroubleshootFixStatus =
				currentStatus?.troubleshootFixStatus === TroubleshootFixStatus.FAILED
					? currentStatus.troubleshootFixStatus
					: progress.troubleshootFixStatus;

			const latestEntry: ResourceLatestStatusEntry = {
				resourceId: progress.resourceId,
				operationId,
				status: progress.status,
				timeStamp: progress.timeStamp,
				error: progress.error,
				troubleshoot: progress.troubleshoot,
				troubleshootFixStatus: preservedTroubleshootFixStatus,
			};

			latestStatusMap[progress.resourceId] = latestEntry;
		}
	}

	return latestStatusMap;
}

export const getTroubleshootFixStatus = (
	isTroubleshootFixInitiated: boolean,
	launchStatus: LaunchStatus
): TroubleshootFixStatus => {
	if (!isTroubleshootFixInitiated) {
		return TroubleshootFixStatus.NOT_APPLICABLE;
	}
	switch (launchStatus) {
		case LaunchStatus.Failed:
			return TroubleshootFixStatus.FAILED;
		case LaunchStatus.Completed:
			return TroubleshootFixStatus.SUCCEEDED;
		default:
			return TroubleshootFixStatus.IN_PROGRESS;
	}
};

export const getTroubleshootFixStatusForLaunchFailure = (
	isTroubleshootFixInitiated: boolean | undefined,
	resource: Resource
): TroubleshootFixStatus => {
	if (!resource.supportsinspectconnection) {
		return TroubleshootFixStatus.NOT_APPLICABLE;
	}

	if (isTroubleshootFixInitiated) {
		return TroubleshootFixStatus.FAILED;
	}
	return TroubleshootFixStatus.NOT_STARTED;
};

export function updateResourceTroubleshootFixStatus(
	resourceLatestStatusMap: ResourceLatestStatusMap,
	resourceId: string,
	newTroubleshootFixStatus: TroubleshootFixStatus
): ResourceLatestStatusMap {
	const currentEntry = resourceLatestStatusMap[resourceId];

	const updatedMap = {
		...resourceLatestStatusMap,
		[resourceId]: {
			...currentEntry,
			troubleshootFixStatus: newTroubleshootFixStatus,
		},
	};

	return updatedMap;
}

const LaunchMechanismValueMap: Record<launchMethod, LaunchMechanism> = {
	[launchMethod.html5]: LaunchMechanism.HTML5,
	[launchMethod.native]: LaunchMechanism.NativeApplication,
	[launchMethod.browserExtension]: LaunchMechanism.BrowserExtension,
	[launchMethod.protocolHandler]: LaunchMechanism.BrowserProtocolHandler,
	[launchMethod.icaFile]: LaunchMechanism.BrowserIcaFileDownload,
};

export const getLaunchMechanism = (workspaceConfiguration: WorkspaceConfiguration) => {
	const launchMethodType = isCitrixChromeApp()
		? launchMethod.native
		: getLaunchMethod(workspaceConfiguration);
	return LaunchMechanismValueMap[launchMethodType];
};

export const createFetchIcaHandler = async (
	payload: any,
	resourceContext: ResourceContext,
	workspaceConfiguration: WorkspaceConfiguration
): Promise<void> => {
	const { resourceId, launchId, correlationId } = payload;

	if (!resourceId) {
		throw new Error('resourceId is required for fetchIca request');
	}

	if (!resourceContext?.resources) {
		throw new Error('Resources not available');
	}

	const resource = resourceContext.resources.find(res => res.id === resourceId);
	if (!resource) {
		throw new Error(`Resource with id ${resourceId} not found`);
	}

	const launchParams: LaunchParams = {
		resource,
		resourceContext,
		workspaceConfiguration,
		launchOptions: {
			fetchIcaParams: {
				isIframeLaunch: true,
				launchId,
				correlationId,
			},
		},
	};

	launchResource(launchParams);
};
